import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Game,
  GameList,
  GameType,
  PopularGameList,
} from "@nestcoinco/hyper-api-gateway-api-client";
import { InfiniteData, UseInfiniteQueryResult } from "@tanstack/react-query";

import "./GamesTabContent.scss";
import { ROUTES } from "../../../utils/constants";
import DownloadAppPrompt from "../../DownloadAppPrompt/DownloadAppPrompt";
import useAuth from "../../../hooks/useAuth";
import useDisclosure from "../../../hooks/useDisclosure";
import useInfinitePopularGamesQuery from "../../../react-query/queries/use-infinite-popular-games";
import GameFilters from "../GameFilters/GameFilters";
import { useGamesPageActions } from "../../../hooks/useGamesPage";
import GamesFeeds from "../GamesFeeds/GamesFeeds";
import useGameDetailsBySlugQuery from "../../../react-query/queries/use-game-details-by-slug";
import useInfiniteFavoriteGamesQuery from "../../../react-query/queries/use-infinite-favorite-games";
import LatestWinsSlider from "../../onboarding/LatestWinsSlider/LatestWinsSlider";
import FavoriteGamesDefinitionDrawer from "../FavoriteGamesDefinitionDrawer/FavoriteGamesDefinitionDrawer";
import useSubscription from "../../../react-query/queries/use-subscription-status";
import useInfiniteGamesQuery from "../../../react-query/queries/use-infinite-games";
import { GamesFilterOptions } from "../../../jotai/atoms/games";
import { ThemeVariant } from "../../../utils/constants/generic-types";
import Skeleton from "../../Skeleton/Skeleton";

const MAX_PAGES_AT_ONCE = 5;
const MAX_DATA_PER_PAGE = 15;

type GamesListData = {
  games: Game[];
  query: UseInfiniteQueryResult<InfiniteData<GameList | PopularGameList>>;
};

export default function GamesTabContent() {
  const { slug } = useParams();
  const { t } = useTranslation();
  const { isLoggedIn: userIsLoggedIn } = useAuth();
  const { data: gameDataFromSlug } = useGameDetailsBySlugQuery({
    slug: slug ?? "",
  });
  const { isSubscribed, isLoading: isSubscriptionLoading } = useSubscription();
  const [isChangingFilter, setIsChangingFilter] = useState<boolean>(false);

  const {
    isOpen: isDownloadAppPromptOpen,
    onOpen: openDownloadAppPrompt,
    onClose: closeDownloadAppPrompt,
  } = useDisclosure();
  const {
    isOpen: isFavoriteGamesDefinitionDrawerOpen,
    onOpen: openFavoriteGamesDefinitionDrawer,
    onClose: closeFavoriteGamesDefinitionDrawer,
  } = useDisclosure();

  const { myFavoriteGames, query: favoriteGamesQuery } =
    useInfiniteFavoriteGamesQuery({
      maxPages: MAX_PAGES_AT_ONCE,
      limit: MAX_DATA_PER_PAGE,
      enabled: userIsLoggedIn,
    });
  const { allGames: freeGames, query: freeGamesQuery } = useInfiniteGamesQuery({
    maxPages: MAX_PAGES_AT_ONCE,
    limit: MAX_DATA_PER_PAGE,
    enabled: userIsLoggedIn && !isSubscribed && !isSubscriptionLoading,
    gameType: GameType.FREE,
  });

  const { selectedGamesFilterId, promptDownloadScenario, changeGamesFilter } =
    useGamesPageActions({
      userHasFavoriteGames:
        !!myFavoriteGames.length && !favoriteGamesQuery.isFetching,
      openDownloadAppPrompt,
      handleFavoriteFilterSelection: openFavoriteGamesDefinitionDrawer,
    });

  const { popularGames, query: popularGamesQuery } =
    useInfinitePopularGamesQuery({
      maxPages: MAX_PAGES_AT_ONCE,
      limit: MAX_DATA_PER_PAGE,
      enabled: selectedGamesFilterId === GamesFilterOptions.POPULAR || !!slug,
    });

  const gamesList: GamesListData = useMemo(() => {
    let data: GamesListData = { games: popularGames, query: popularGamesQuery };
    switch (selectedGamesFilterId) {
      case GamesFilterOptions.FAVORITES:
        data = { games: myFavoriteGames, query: favoriteGamesQuery };
        break;
      case GamesFilterOptions.FREE:
        data = { games: freeGames, query: freeGamesQuery };
        break;
      default:
        data = { games: popularGames, query: popularGamesQuery };
    }
    return data;
  }, [
    popularGames,
    popularGamesQuery,
    selectedGamesFilterId,
    myFavoriteGames,
    favoriteGamesQuery,
    freeGames,
    freeGamesQuery,
  ]);

  const handleFilterSelection = useCallback(
    (filterId: GamesFilterOptions) => {
      if (filterId === selectedGamesFilterId) return;

      setIsChangingFilter(true);
      changeGamesFilter(filterId);
      setTimeout(() => setIsChangingFilter(false), 1500);
    },
    [selectedGamesFilterId, changeGamesFilter]
  );

  useEffect(() => {
    if (
      selectedGamesFilterId === GamesFilterOptions.FAVORITES &&
      myFavoriteGames.length === 0 &&
      !favoriteGamesQuery.isFetching
    ) {
      handleFilterSelection(GamesFilterOptions.POPULAR);
    }
  }, [
    selectedGamesFilterId,
    myFavoriteGames.length,
    favoriteGamesQuery.isFetching,
    handleFilterSelection,
  ]);

  return (
    <>
      <div className="MobileOnly GamesTabContent">
        <div className="GamesTabHeader">
          <GameFilters
            selectedFilterId={selectedGamesFilterId}
            handleFilterSelection={handleFilterSelection}
          />
          {!userIsLoggedIn && (
            <Link
              to={ROUTES.ROOT.LOG_IN}
              className="text-capitalize text-decoration-none allow-pointer-events LoginCta"
            >
              {t("login")}
            </Link>
          )}
          <LatestWinsSlider className="PlayersStats" />
        </div>

        <Skeleton
          width="100%"
          height="100%"
          isLoading={isChangingFilter}
          theme={ThemeVariant.DARK}
          borderRadius={0}
          style={{ position: "absolute", top: 0, left: 0, zIndex: 1 }}
          shimmerStyle={{
            zIndex: 1,
            top: -25,
            rotate: "20deg",
            height: "102.5%",
          }}
        >
          <GamesFeeds
            initialGame={gameDataFromSlug}
            gamesList={gamesList.games}
            gamesListQuery={gamesList.query}
            maxPages={MAX_PAGES_AT_ONCE}
            selectedFilterId={selectedGamesFilterId}
            openDownloadAppPrompt={openDownloadAppPrompt}
          />
        </Skeleton>
      </div>

      <DownloadAppPrompt
        isOpen={isDownloadAppPromptOpen}
        onOpen={openDownloadAppPrompt}
        onClose={closeDownloadAppPrompt}
        promptDownloadScenario={promptDownloadScenario}
      />
      <FavoriteGamesDefinitionDrawer
        isOpen={isFavoriteGamesDefinitionDrawerOpen}
        onClose={closeFavoriteGamesDefinitionDrawer}
      />
    </>
  );
}
